import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, RefreshCw, Loader2, AlertTriangle, Radio, VolumeX, ServerCrash, Layers, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  adminObservabilitySummary,
  adminObservabilityRecent,
} from '../lib/api';

/**
 * Admin · Observability dashboard
 *
 * Purpose: turn the growing `telemetry_events` MongoDB collection into
 * something you can eyeball in 5 seconds and decide "is anything on
 * fire?".
 *
 * Four panels, all fed from `GET /api/admin/observability/summary`:
 *   1. Events-by-kind — top-line count for every distinct kind so a
 *      spike in any category jumps out.
 *   2. Audio failures by label — Instagram / Facebook / iOS unlock /
 *      etc. Tells you which platform is blocking sound the most.
 *   3. External-API failures — Resend / OpenAI / Cloudinary errors
 *      grouped by (provider, endpoint). Includes the most recent
 *      error string so you can spot "template not published" without
 *      opening Mongo.
 *   4. UI errors — React ErrorBoundary trips + `ui.error.*` events
 *      from the frontend, with the last route + error message.
 *
 * Time window picker (24h / 7d / 30d) — sends the `hours` query param
 * to the backend. Refresh button re-fetches without a full reload.
 *
 * Each row in the audio-failures / external-API / UI-errors panels is
 * clickable — opens a drill-in modal showing the last N raw events for
 * that kind so admins can spot patterns without going to Mongo.
 */
const WINDOWS = [
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 168 },
  { label: 'Last 30 days', hours: 720 },
];

const AdminObservability = () => {
  const { user, loading: authLoading } = useAuth();
  const [hours, setHours] = useState(168);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null); // { kind, items, loading }

  const load = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const d = await adminObservabilitySummary(hours);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load summary');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'admin') load();
  }, [user, hours]);

  const openDrilldown = async (kind) => {
    setDrilldown({ kind, items: [], loading: true });
    try {
      const d = await adminObservabilityRecent(kind, 50);
      setDrilldown({ kind, items: d?.items || [], loading: false });
    } catch (e) {
      setDrilldown({ kind, items: [], loading: false, error: e.message });
    }
  };

  if (authLoading) return <div className="p-8 text-center text-zinc-500">Loading…</div>;
  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="admin-observability-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Activity size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-observability-title">Admin · Observability</h1>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            data-testid="observability-window-picker"
            className="h-9 px-3 rounded-full border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
          >
            {WINDOWS.map(w => <option key={w.hours} value={w.hours}>{w.label}</option>)}
          </select>
          <button
            type="button"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            data-testid="observability-refresh-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Top-line total */}
      {data && (
        <div className="mb-5 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 flex items-baseline gap-2">
          <span className="text-4xl font-bold" data-testid="observability-total-events">{data.total_events.toLocaleString()}</span>
          <span className="text-sm text-zinc-500">telemetry events over this window · generated {formatWhen(data.generated_at)}</span>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4" data-testid="observability-error">
          <AlertTriangle size={14} className="inline mr-1" /> {error}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-zinc-500">
          <Loader2 className="animate-spin inline mr-2" size={16} /> Loading rollups…
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Panel 1 — Events by kind */}
          <Panel icon={Layers} title="Events by kind" testid="panel-by-kind" empty={data.by_kind.length === 0}>
            {data.by_kind.map(row => (
              <button
                key={row.kind}
                type="button"
                onClick={() => openDrilldown(row.kind)}
                data-testid={`kind-row-${row.kind}`}
                className="w-full flex items-center gap-3 py-2 px-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-left"
              >
                <code className="text-xs font-mono text-zinc-700 dark:text-zinc-300 truncate flex-1">{row.kind}</code>
                {row.error_count > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-semibold">
                    {row.error_count} err
                  </span>
                )}
                <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{row.count}</span>
              </button>
            ))}
          </Panel>

          {/* Panel 2 — Audio failures by label */}
          <Panel icon={VolumeX} title="Audio failures by platform" testid="panel-audio" empty={data.audio_failures_by_label.length === 0}>
            {data.audio_failures_by_label.map((row, i) => (
              <button
                key={`${row.label}-${row.reason}-${i}`}
                type="button"
                onClick={() => openDrilldown('audio_failure')}
                data-testid={`audio-row-${row.label.replace(/\s+/g, '-').toLowerCase()}`}
                className="w-full flex items-center gap-3 py-2 px-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-left"
              >
                <span className="text-sm text-zinc-800 dark:text-zinc-200 truncate flex-1">{row.label}</span>
                <code className="text-[10px] font-mono text-zinc-500 shrink-0">{row.reason}</code>
                <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{row.count}</span>
              </button>
            ))}
          </Panel>

          {/* Panel 3 — External-API failures */}
          <Panel icon={ServerCrash} title="External-API failures" testid="panel-ext-api" empty={data.external_api_failures.length === 0}>
            {data.external_api_failures.map((row, i) => (
              <div
                key={`${row.provider}-${row.endpoint}-${i}`}
                data-testid={`ext-api-row-${row.provider}`}
                className="w-full py-2 px-2 rounded"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-zinc-700 dark:text-zinc-300">{row.provider}</span>
                  <span className="text-zinc-400">/</span>
                  <code className="text-xs font-mono text-zinc-600 dark:text-zinc-400 truncate flex-1">{row.endpoint}</code>
                  <span className="text-[10px] text-zinc-500">{row.avg_duration_ms}ms avg</span>
                  <span className="text-sm font-semibold tabular-nums text-red-700 dark:text-red-400">{row.count}</span>
                </div>
                <div className="text-[11px] text-zinc-500 truncate">
                  <span className="font-mono">{row.error_type}</span>
                  {row.last_error ? <span className="ml-1">— {row.last_error}</span> : null}
                </div>
              </div>
            ))}
          </Panel>

          {/* Panel 4 — UI errors */}
          <Panel icon={Radio} title="UI errors" testid="panel-ui" empty={data.ui_errors.length === 0}>
            {data.ui_errors.map(row => (
              <button
                key={row.kind}
                type="button"
                onClick={() => openDrilldown(row.kind)}
                data-testid={`ui-error-row-${row.kind}`}
                className="w-full flex flex-col gap-1 py-2 px-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-left"
              >
                <div className="flex items-center gap-3 w-full">
                  <code className="text-xs font-mono text-zinc-700 dark:text-zinc-300 truncate flex-1">{row.kind}</code>
                  <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{row.count}</span>
                </div>
                {(row.last_route || row.last_error) && (
                  <div className="text-[11px] text-zinc-500 truncate w-full">
                    {row.last_route ? <span className="font-mono">{row.last_route}</span> : null}
                    {row.last_error ? <span className="ml-1">— {row.last_error}</span> : null}
                  </div>
                )}
              </button>
            ))}
          </Panel>
        </div>
      ) : null}

      {/* Cross-links */}
      <div className="mt-6 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
        <Link to="/admin/feedback" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Feedback</Link>
        <Link to="/admin/email-templates" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Email Templates</Link>
        <Link to="/admin/activity" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Activity</Link>
      </div>

      {/* Drilldown modal */}
      {drilldown && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDrilldown(null)}
          data-testid="observability-drilldown-backdrop"
        >
          <div
            className="bg-white dark:bg-zinc-900 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-3">
              <code className="text-sm font-mono flex-1">{drilldown.kind}</code>
              <span className="text-xs text-zinc-500">{drilldown.items.length} recent</span>
              <button
                type="button"
                onClick={() => setDrilldown(null)}
                data-testid="observability-drilldown-close"
                className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {drilldown.loading ? (
                <div className="py-8 text-center text-zinc-500">
                  <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
                </div>
              ) : drilldown.items.length === 0 ? (
                <div className="py-8 text-center text-zinc-500 text-sm">No recent events.</div>
              ) : (
                drilldown.items.map(item => (
                  <div key={item.id} className="text-[11px] font-mono bg-zinc-50 dark:bg-zinc-800/40 rounded p-2 border border-zinc-200 dark:border-zinc-800">
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-zinc-600 dark:text-zinc-400 mb-1">
                      <span>{formatWhen(item.created_at)}</span>
                      {item.user_id && <span className="text-amber-700 dark:text-amber-400">user:{item.user_id.slice(-8)}</span>}
                      {item.request_id && <span>req:{item.request_id.slice(0, 8)}</span>}
                      {item.level && <span>lvl:{item.level}</span>}
                      {item.route && <span>route:{item.route}</span>}
                    </div>
                    {item.error_message && (
                      <div className="text-red-700 dark:text-red-400 whitespace-pre-wrap break-words">{item.error_message}</div>
                    )}
                    {item.provider && (
                      <div>{item.provider} · {item.endpoint} · {item.status} · {item.duration_ms}ms</div>
                    )}
                    {item.label && item.reason && (
                      <div>{item.label} · {item.reason} · {item.phase}</div>
                    )}
                    {item.meta && Object.keys(item.meta).length > 0 && (
                      <div className="text-zinc-500">meta: {JSON.stringify(item.meta)}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -------- helpers --------
function Panel({ icon: Icon, title, testid, empty, children }) {
  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-amber-500" />
        <div className="text-sm font-semibold">{title}</div>
      </div>
      {empty ? (
        <div className="py-6 text-center text-zinc-500 text-xs">No events in this window.</div>
      ) : (
        <div className="space-y-0.5">{children}</div>
      )}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return 'never';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  } catch {
    return iso;
  }
}

export default AdminObservability;
